import { useCallback, useMemo, useState } from "react";

import type { DateRange } from "@/components/ui/calendar/types/calendar-range";

import type { SearchTypeForECReceiptList } from "../../../../../model/constants";
import type { OnSearchSalesMastListProps } from "../../../../../model/use-case/use-search-reservation-list";

/**
 * 日付での検索
 */
type DateRangeCase = {
	from: string | undefined;
	to: string | undefined;

	receiptID: string;
};

type SearchState = DateRangeCase;

type Props = {
	onSearchSalesMastList: (props: OnSearchSalesMastListProps) => void;

	searchType: SearchTypeForECReceiptList;
	onSetSearchType: (type: SearchTypeForECReceiptList) => void;
};

export const useHandleSearchActions = ({
	onSearchSalesMastList,
	searchType,
	onSetSearchType, //
}: Props) => {
	const [searchState, setSearchState] = useState<SearchState>({ from: "", to: "", receiptID: "" });

	/**
	 * 収支検索タイプの変更
	 */
	const handleChangeSearchType = useCallback(
		(searchType: string) => {
			onSetSearchType(searchType as SearchTypeForECReceiptList);
		},
		[onSetSearchType],
	);

	/**
	 * 日付の変更
	 */
	const handleChangeFromAndTo = useCallback(({ from, to }: DateRange) => {
		setSearchState((prev) => ({ ...prev, from: from, to: to }));
	}, []);

	/**
	 * レシートIDの変更
	 */
	const handleChangeReceiptID = useCallback((receiptID: string) => {
		setSearchState((prev) => ({ ...prev, receiptID }));
	}, []);

	/**
	 * 日付での検索
	 */
	const onSearchSalesMastListByRangeDate = useCallback(
		({ from, to }: { from: string; to: string }) => {
			onSearchSalesMastList({ startDt: from, endDt: to });
		},
		[onSearchSalesMastList],
	);

	/**
	 * レシートIDでの検索
	 */
	const onSearchSalesMastListByReceiptID = useCallback(
		(receiptID: string) => {
			onSearchSalesMastList({ receiptID });
		},
		[onSearchSalesMastList],
	);

	/**
	 * 収支の検索
	 */
	const handleClickSearchSalesMastList = useCallback(() => {
		if (searchType === "startDtAndEndDt") {
			if (!searchState.from || !searchState.to) return;

			const from = searchState.from;
			/**
			 * 日付の範囲が指定されていない場合は、開始日と終了日を同じにする
			 */
			const to = searchState.to ?? from;
			onSearchSalesMastListByRangeDate({ from, to });
		} else if (searchType === "receiptID") {
			if (!searchState.receiptID) return;

			// 予約IDでの検索
			onSearchSalesMastListByReceiptID(searchState.receiptID);
		}
	}, [onSearchSalesMastListByRangeDate, onSearchSalesMastListByReceiptID, searchState, searchType]);

	const disabled = useMemo(() => {
		if (searchType === "receiptID") {
			return !searchState.receiptID;
		}

		return !(searchState.from && searchState.to);
	}, [searchState.from, searchState.receiptID, searchState.to, searchType]);

	return {
		searchState,

		searchType,
		onSetSearchType,

		//
		disabled,
		//
		// 収支タイプの変更
		handleChangeSearchType,
		// レシートIDの変更
		handleChangeReceiptID,
		// 日付の変更
		handleChangeFromAndTo,
		// 収支の検索
		handleClickSearchSalesMastList,
	};
};
